import React from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Hidden } from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import { BeneficiarySettings } from '../model/epi-props';
import { BeneficiaryCreateFormModel, BeneficiaryType, RelationshipType } from '../model/beneficiary-create-models';
import { FormWrapper } from '../../../../../global/components/form-wrapper';
import { FormTypes } from '../../../../account-info/models/form-models';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { useGpsType } from '../../../../../global/hooks/useGpsType';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { PageContainer } from '../../../../../global/components/layout/page-container';
import { MainContent } from '../../../../../global/components/layout/main-content-container';
import { CreateBeneficiary, GetBeneficiaryTypeOptions, getInitialFormModel, GetValidationSchemaForCreateBeneficiary } from './helper/create-beneficiary-helper';
import { FormField } from '../../../../../global/components/form-element/form-field';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { CreateBeneficiaryFieldsFactory } from './create-beneficiary-fields-factory';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { useApiResponseError } from '../../../../../global/hooks/useApiError';
import { ArcRoutes } from '../../../constants/routes';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { AccountInfoRoutes } from '../../../../account-info/constants/routes';
import { getBeneficiariesRelationships } from '../../../../../global/ducks/benadmin-api';
import { setBeneficiariesRelationships } from '../beneficiary-slices';


interface Props {
	settings: BeneficiarySettings
}
export function CreateBeneficiaryForm(props: Props) {

	const { settings } = props;
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
	const dispatch = useDispatch();
	const gpsItem = useGpsType(GpsType.BENEFICIARY_CREATE);
	const validationSchema = GetValidationSchemaForCreateBeneficiary(settings, labelSettings);
	const guid = useSelector<TcsmStates, string>(state => state?.authenticatedState?.clientGuid);
	const errors = useApiResponseError(FormTypes.BENEFICIARY_CREATE);
	const history = useHistory();
	const basePath = useBasePath(`${AccountInfoRoutes.MyCpgRoot}`);
	const { state } = useLocation<any>();
	
	const navigateBackToPlan = () => {
		if (state?.beneficiaryUpdate && state?.returnPath){
			history.push(state?.returnPath, 
				{
					beneficiaryUpdate: {
						...state?.beneficiaryUpdate,
						newBeneficiaryAdded: true,
					},
					beneficiaryCreated: true,
				});

			return;
		}
		
		history.push(`${basePath}${ArcRoutes.Beneficiary}?status=success`);
	};

	const onSuccess = () => {
		dispatch(getBeneficiariesRelationships(guid, (res => dispatch(setBeneficiariesRelationships(res?.data)))));
		navigateBackToPlan();
	};
	return (
		<PageContainer>
			<MainContent>
				<Formik
					initialValues={getInitialFormModel()}
					onSubmit={(values: BeneficiaryCreateFormModel) => CreateBeneficiary(values, dispatch, guid, onSuccess)}
					validationSchema={validationSchema}
					enableReinitialize
					initialErrors={errors}
				>
					{(formProps: FormikProps<BeneficiaryCreateFormModel>) => (

						<Form>
							<FormWrapper
								formikProps={formProps}
								submitText={settings?.saveButtonText}
								cancelText={settings?.cancelButtonText}
								onCancel={() => {
									window.location.reload();
								}}
								formType={FormTypes.BENEFICIARY_CREATE}
								hideButtons={
									(formProps?.values?.relationship === RelationshipType.Spouse || formProps?.values?.relationship === RelationshipType.Domestic)
									&& formProps?.values?.beneficiary_type === BeneficiaryType.Person
								}
							>
								<PageHeader
									headerText={settings?.createNewBeneficiaryPageTitle}
									description={settings?.createNewBeneficiaryPageDescription}
								/>
								<Hidden mdUp>
									<SideBarContent>
										<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
									</SideBarContent>
								</Hidden>
								<Grid>
									<ContentSpacer>
										<FormField
											name='beneficiary_type'
											label={settings?.beneficiaryTypeLabel}
											component='radioGroup'
											options={GetBeneficiaryTypeOptions(settings)}
											touched={formProps.touched}
											errors={formProps.errors}
											tooltip={{
												title: settings.beneficiaryTypeHelperText,
											}}
											required
										/>
									</ContentSpacer>
									<CreateBeneficiaryFieldsFactory type={formProps?.values?.beneficiary_type} settings={settings} formProps={formProps} />
								</Grid>
							</FormWrapper>
						</Form>
					)}
				</Formik>
			</MainContent>
			<Hidden smDown>
				<SideBarContent>
					<div className='sidebar'>
						<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
					</div>
				</SideBarContent>
			</Hidden>
		</PageContainer>
	);
}